import React from 'react'
import {
  Caption,
  Col,
  Strong,
  NextIcon,
  Row,
  OverdueIcon,
  withTooltip,
  TaskStatusTag,
  withResponsiveHelper,
} from '@modifi/ui'
import {useTranslation, formatDate} from '@modifi/plugin-i18n'
import {airBlue, warning, semiDarkGray} from '@modifi/colors'
import {TaskCategory, TaskStatus} from '@modifi/enums'
import {
  TaskContextParameterView,
  TaskRelatedEntityView,
  TaskView,
} from '@modifi/onboarding-api-clients'

import {Card, UnstyledLink} from '../../styles'
import {getTaskLink} from './utils'

const getTaskTranslationProps = (
  category: TaskCategory,
  contextParams: TaskContextParameterView[]
): Record<string, string | undefined> => {
  if (contextParams?.length > 0) {
    if (
      [TaskCategory.COMPANY_FINANCIAL_REVIEW, TaskCategory.COMPANY_GROUP_FINANCIAL_REVIEW].includes(
        category
      )
    ) {
      return {year: contextParams[0]?.value?.replace('_', '-')}
    }
    if (TaskCategory.TRADE_PARTNER_INSURANCE_LIMIT_HIGH_AMOUNT_APPLICATION === category) {
      return {tradePartnerCompanyName: contextParams[0]?.value}
    }
  }

  return {}
}

const TooltipIcon = withTooltip(OverdueIcon) as React.ComponentType<any>

export type TasksListProps = {
  tasks?: TaskView[]
  isLgOrSmaller: boolean
}

const TasksList: React.FC<TasksListProps> = ({tasks = [], isLgOrSmaller}) => {
  const {t} = useTranslation(['charon', 'plugin-forms'])
  return (
    <>
      {tasks?.map(task => {
        const isOverdue =
          task?.dueDateOn &&
          Math.floor(
            (new Date().getTime() - new Date(task.dueDateOn).getTime()) / (1000 * 60 * 60 * 24)
          ) > 0

        return (
          <Row my={16} key={task?.externalId}>
            <Col>
              <UnstyledLink
                toWithQueryParams={getTaskLink(
                  task?.category as TaskCategory,
                  task?.relatedEntities as TaskRelatedEntityView[]
                )}
              >
                <Card alignItems="center" small>
                  {task?.status === TaskStatus.TODO && (
                    <Col lg={1}>
                      <TooltipIcon
                        color={warning}
                        height={32}
                        width={32}
                        tooltipTheme="admin"
                        tooltipPlacement="right"
                        tooltipContent={
                          task?.dueDateOn && (
                            <>
                              {t(
                                `overview.${task?.category}.${
                                  isOverdue ? 'overdue' : 'due-date'
                                }.label`,
                                // @ts-expect-error
                                {dueDate: formatDate(task.dueDateOn)}
                              )}
                            </>
                          )
                        }
                      />
                    </Col>
                  )}
                  {task?.status === TaskStatus.IN_REVIEW && isLgOrSmaller && (
                    <Col col={6}>
                      <Row mb={8}>
                        <Col>
                          <TaskStatusTag status={TaskStatus.IN_REVIEW} />
                        </Col>
                      </Row>
                    </Col>
                  )}
                  {/* eslint-disable-next-line no-nested-ternary */}
                  <Col col={task?.status === TaskStatus.TODO ? 10 : isLgOrSmaller ? 11 : 8}>
                    <Row>
                      <Col>
                        <Strong>
                          {t(
                            `overview.task-type.${task?.category}.headline`,
                            getTaskTranslationProps(
                              task?.category as TaskCategory,
                              task?.contextParameters || []
                            )
                          )}
                        </Strong>
                      </Col>
                    </Row>
                    <Row>
                      <Col>
                        <Caption color={semiDarkGray}>
                          {t(`overview.task-type.${task?.category}.${task?.status}.description`)}
                        </Caption>
                      </Col>
                    </Row>
                  </Col>
                  {task?.status === TaskStatus.IN_REVIEW && !isLgOrSmaller && (
                    <Col lg={3}>
                      <TaskStatusTag status={TaskStatus.IN_REVIEW} />
                    </Col>
                  )}
                  <Col col={1}>
                    <NextIcon color={airBlue} />
                  </Col>
                </Card>
              </UnstyledLink>
            </Col>
          </Row>
        )
      })}
    </>
  )
}

export default withResponsiveHelper(TasksList)
